import React, { useEffect, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import { FiCheck, FiClock, FiCopy, FiFileText, FiGift, FiInfo, FiLock, FiMail, FiMapPin, FiShield } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { isDTS } from "../../../utility/platform";
import { findCmsEntryBySlug } from "../../../utility/cmsContent";
import { getAmoePolicy, getSweepsCoinName } from "../../../utility/complianceCopy";
import { useComplianceConfig } from "../../../utility/useComplianceConfig";
import Captcha from "../../common-components/Captcha";
import DashboardLayout from "../../layout/dashboardLayout";
import { generateAmoeCode, getMyAmoeStatus } from "../../actions/amoe";
import { getMyKycStatus } from "../../actions/kyc";
import { toastAlert } from "../../lib/toastAlert";
import "../../../style/dts-ui.css";

function AmoeRequest() {
  useComplianceConfig();
  const policy = getAmoePolicy();
  const cmsData = useSelector((state) => state.cmsData?.cmsData);
  const sidebarEntry = findCmsEntryBySlug(cmsData, "amoe-how-it-works");
  const sidebarHtml = DOMPurify.sanitize(sidebarEntry?.content || "");
  const sidebarProbe = document.createElement("div");
  sidebarProbe.innerHTML = sidebarHtml;
  const hasSidebarContent = Boolean(sidebarProbe.textContent.trim() || sidebarProbe.querySelector("img"));
  const [status, setStatus] = useState(null); // { code, status, expiresAt, ... } | null
  const [kycApproved, setKycApproved] = useState(true);
  const [loading, setLoading] = useState(true);
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const captchaRef = useRef(null);

  const refresh = async () => {
    // Code generation is KYC-gated on the server (Stage 6). Reading the gate up
    // front turns an unverified user's experience from "press the button, get
    // rejected" into a clear next step.
    const [amoe, kyc] = await Promise.all([getMyAmoeStatus(), getMyKycStatus()]);
    setStatus(amoe.result);
    setKycApproved(kyc?.result?.userStatus === "approved");
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleRequest = async () => {
    if (!captchaToken) {
      toastAlert("error", "Please complete the CAPTCHA challenge.", "amoe");
      return;
    }
    setSubmitting(true);
    const res = await generateAmoeCode(captchaToken);
    setSubmitting(false);
    captchaRef.current?.reset();
    setCaptchaToken("");
    if (res.status) {
      // Render the newly generated code immediately. The follow-up read remains
      // useful for reconciliation, but the UI must not depend on it to move out
      // of the request form after a successful POST.
      setStatus({ ...res.result, status: "active" });
      toastAlert("success", "AMOE code generated.", "amoe");
      refresh();
    } else {
      toastAlert("error", res.message || "Could not generate a code.", "amoe");
      if (res.kycStatus) refresh();
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(status.code);
      setCopied(true);
    } catch (_) {
      toastAlert("error", "Could not copy the code. Please select and copy it manually.", "amoe");
    }
  };

  if (!isDTS()) return null;
  if (loading) {
    return (
      <DashboardLayout>
        <main className="dts-compliance-page">
          <div className="dts-compliance-shell" aria-busy="true">
            <div className="dts-skeleton dts-skeleton--heading" />
            <div className="dts-skeleton dts-skeleton--card" />
          </div>
        </main>
      </DashboardLayout>
    );
  }

  const hasActiveCode = status && status.status === "active";
  const activePolicy = hasActiveCode ? {
    ...policy,
    validityDays: status.validityDays || policy.validityDays,
    creditAmount: status.creditAmount || policy.creditAmount,
    mailingAddress: status.mailingAddress || policy.mailingAddress,
  } : policy;

  return (
    <DashboardLayout>
      <main className="dts-compliance-page dts-amoe">
        <ToastContainer />
        <div className="dts-compliance-shell">
        <header className="dts-compliance-header dts-amoe-hero">
          <div className="dts-amoe-hero__copy">
            <span className="dts-eyebrow"><FiMail aria-hidden="true" /> No purchase necessary</span>
            <h1 className="dts-page__title">Mail-in entry request</h1>
            <p className="dts-page__lead">Request a unique code, then include it with your handwritten mail-in entry.</p>
            <div className="dts-amoe-journey" aria-label="Mail-in entry process">
              <span><FiFileText aria-hidden="true" /> Request a code</span>
              <span><FiCopy aria-hidden="true" /> Write your entry</span>
              <span><FiMail aria-hidden="true" /> Mail your card</span>
            </div>
          </div>
          <svg className="dts-amoe-hero__art" viewBox="0 0 300 240" aria-hidden="true" focusable="false">
            <defs>
              <linearGradient id="amoe-envelope" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#e1f3ff"/><stop offset="1" stopColor="#5b9ee5"/></linearGradient>
              <linearGradient id="amoe-card" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#fff"/><stop offset="1" stopColor="#d7ebff"/></linearGradient>
            </defs>
            <circle cx="244" cy="34" r="3" fill="#8ac8ff"/><circle cx="40" cy="110" r="2" fill="#d5efff"/><circle cx="275" cy="138" r="2" fill="#d5efff"/>
            <path d="M36 86 L150 14 L264 86 L264 212 L36 212 Z" fill="#0754ab" stroke="#8fcaff" strokeWidth="2"/>
            <g transform="rotate(9 150 103)"><rect x="69" y="27" width="168" height="151" rx="11" fill="url(#amoe-card)"/><path d="M104 106 H202 M104 122 H187 M104 138 H170" stroke="#9ac5ee" strokeWidth="5" strokeLinecap="round"/><path d="M151 56 l7 10 10 -8 -2 18 h-31 l-2 -18 10 8z" fill="#e8ae3e"/><text x="152" y="94" fill="#0b3672" fontFamily="Arial,sans-serif" fontSize="14" fontWeight="800" textAnchor="middle">DREAMSPORTS</text></g>
            <path d="M36 86 L150 164 L264 86 V211 H36 Z" fill="url(#amoe-envelope)"/>
            <path d="M36 211 L125 142 L150 164 L175 142 L264 211" fill="none" stroke="#e6f6ff" strokeWidth="2" opacity=".8"/>
            <path d="M36 211 L126 143 M264 211 L174 143" stroke="#75ace7" strokeWidth="2" opacity=".7"/>
          </svg>
        </header>

        <div className="dts-compliance-grid">
          <section className="dts-panel dts-panel--primary" aria-label="AMOE request status">
            {/* Tones are the DTS dark-theme alert (style/dts-ui.css). */}
            {hasActiveCode ? (
              <div className="dts-code-card" role="status" aria-label="Active request code">
                <div className="dts-code-card__header">
                  <div className="dts-code-card__heading"><span className="dts-code-card__heading-icon"><FiFileText aria-hidden="true" /></span><div><h2>Active request code</h2><p>Use this code on your handwritten mail-in entry.</p></div></div>
                  <span className="dts-status-pill"><span aria-hidden="true" /> Active</span>
                </div>
                <div className="dts-code-card__code-line">
                  <div className="dts-code-card__value">{status.code}</div>
                  <button type="button" className="dts-code-card__copy" onClick={copyCode}><span aria-hidden="true">{copied ? <FiCheck /> : <FiCopy />}</span>{copied ? "Copied" : "Copy code"}</button>
                </div>
                <div className="dts-code-card__facts">
                  <div className="dts-code-card__fact"><FiClock aria-hidden="true" /><div><span>Expires</span><strong>{new Date(status.expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</strong><small>{activePolicy.validityDays} days from issue</small></div></div>
                  <div className="dts-code-card__fact"><FiGift aria-hidden="true" /><div><span>Approved entry awards</span><strong>{activePolicy.creditAmount} {getSweepsCoinName()}</strong><small>After your mailed entry is approved</small></div></div>
                </div>
                <p className="dts-code-card__hint"><FiInfo aria-hidden="true" /> Copy this code exactly onto your handwritten index card. Only one active code is allowed per account.</p>
              </div>
            ) : status && status.status === "processed" ? (
              <div className="dts-alert dts-alert--success" role="status">
                <strong className="dts-alert__title">Code processed</strong>
                <p className="dts-alert__body">Your last entry was approved and {status.creditedAmount} {getSweepsCoinName()} was added to your promotional balance.</p>
              </div>
            ) : status && status.status === "rejected" ? (
              <div className="dts-alert dts-alert--warning" role="status">
                <strong className="dts-alert__title">Entry not approved</strong>
                <p className="dts-alert__body">Your last entry could not be processed. Review the instructions before requesting a new code.</p>
              </div>
            ) : status && status.status === "expired" ? (
              <div className="dts-alert dts-alert--neutral" role="status">
                <strong className="dts-alert__title">Code expired</strong>
                <p className="dts-alert__body">Your previous code expired after {status.validityDays || policy.validityDays} days. You can request a new code below.</p>
              </div>
            ) : (
              <div className="dts-panel__intro">
                <div className="dts-panel__icon"><FiMail aria-hidden="true" /></div>
                <div><h2 className="dts-panel__title">Request your code</h2><p className="dts-panel__copy">Each approved handwritten entry awards {policy.creditAmount} {getSweepsCoinName()} to your promotional balance.</p></div>
              </div>
            )}

            {!hasActiveCode && !kycApproved && (
              <div className="dts-gate-card" role="status">
                <div className="dts-gate-card__icon"><FiShield aria-hidden="true" /></div>
                <div>
                  <h2>Identity verification required</h2>
                  <p>Complete a secure identity check before requesting an AMOE code.</p>
                  <Link className="dts-btn dts-btn--compact" to="/kyc">Verify your identity</Link>
                </div>
              </div>
            )}

            {!hasActiveCode && !policy.mailingAddress && (
              <div className="dts-alert dts-alert--warning" role="status">
                <strong className="dts-alert__title">Mailing address not configured</strong>
                <p className="dts-alert__body">New mail-in codes are unavailable until the processing address is published.</p>
              </div>
            )}

            {!hasActiveCode && kycApproved && policy.mailingAddress && (
              <div className="dts-action-box">
                <div className="dts-action-box__heading"><FiLock aria-hidden="true" /><div><strong>Protected request</strong><span>Complete the CAPTCHA to generate your unique code.</span></div></div>
                <Captcha ref={captchaRef} onChange={setCaptchaToken} className="dts-captcha" />
                <button type="button" className="dts-btn" disabled={submitting} onClick={handleRequest}>
                  {submitting ? "Requesting…" : "Request AMOE code"}
                </button>
              </div>
            )}
          </section>

          <aside className="dts-panel dts-panel--aside" aria-label="Mail-in entry instructions">
            <div className="dts-aside-heading"><div>{!hasSidebarContent && <span className="dts-aside-kicker">Three simple steps</span>}<h2 className="dts-panel__title">{hasSidebarContent ? sidebarEntry.title || "How mail-in entry works" : "How mail-in entry works"}</h2></div><Link className="dts-text-link" to="/info/amoe">Full rules</Link></div>
            {hasSidebarContent ? (
              <>
                <div className="dts-aside-cms-content">{parse(sidebarHtml)}</div>
                <p className="dts-aside-policy-note">Codes are valid for {activePolicy.validityDays} days. Approved entries award {activePolicy.creditAmount} {getSweepsCoinName()}.</p>
              </>
            ) : (
              <ol className="dts-step-list">
                <li><span>1</span><div><strong>Request a code</strong><small>Codes are unique and expire after {activePolicy.validityDays} days.</small></div></li>
                <li><span>2</span><div><strong>Handwrite your card</strong><small>Use a 4×6 card and include all required account details.</small></div></li>
                <li><span>3</span><div><strong>Mail one entry</strong><small>Place one card in a standard #10 envelope.</small></div></li>
              </ol>
            )}
            <div className="dts-privacy-note"><FiMapPin aria-hidden="true" /><span>{activePolicy.mailingAddress ? <>Mail to:<strong className="dts-amoe-address">{activePolicy.mailingAddress}</strong></> : "Mailing address not configured."}</span></div>
          </aside>
        </div>
        </div>
      </main>
    </DashboardLayout>
  );
}

export default AmoeRequest;
